import { USER_ALREADY_EXISTS_ERROR_CODE } from "@/axios/constants";
import PageTransition from "@/components/ui/PageTransition";
import { Button, buttonVariants } from "@/components/ui/button/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import FloatingInput from "@/components/ui/floatingInput";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form/form";
import PasswordWithRequirements from "@/components/ui/passwordRequirements";
import PhoneInputField from "@/components/ui/phone-input/phone-input";
import { removeDialCode } from "@/components/ui/phone-input/phone-input.utils";
import { Progress } from "@/components/ui/progress";
import SubmitButton from "@/components/ui/submit-button";
import { useToast } from "@/components/ui/use-toast";
import { PHONE_NUMBER_STATEMENT } from "@/constants/common-constants";
import { COUNTRIES } from "@/constants/countries";
import { MainContext } from "@/context/user-information/user-information-context";
import { useSignUpUser } from "@/features/register/hooks/useSignUpUser";
import { getHelpCenterUrl, getTermsOfServiceUrl } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { CheckCircle } from "lucide-react";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const formSchema = z
	.object({
		email: z
			.string()
			.min(2, { message: "Email must be at least 2 characters." })
			.trim()
			.email({ message: "Please enter a valid email address." }),
		password: z
			.string()
			.min(8, { message: "Password must be at least 8 characters." })
			.max(60, { message: "Password is too long" })
			.refine((password) => /[A-Z]/.test(password), {
				message: "Password must contain at least one uppercase letter.",
			})
			.refine((password) => /[a-z]/.test(password), {
				message: "Password must contain at least one lowercase letter.",
			})
			.refine((password) => /[0-9]/.test(password), {
				message: "Password must contain atleast one number.",
			})
			.refine((password) => /[!@#$%^&*]/.test(password), {
				message: "Password must contain atleast one special character.",
			}),
		confirmPassword: z.string(),
		phone: z
			.string({ message: "Please enter a phone number" })
			.min(10, "Please enter a phone number")
			.max(15, "Phone number must not exceed 15 digits"),
		countryCode: z
			.number({ message: "Please enter a phone number" })
			.min(1, "Please select a valid country"),
		ageConfirmed: z.boolean().refine((val) => val === true, {
			message: "You must be 21 or older to continue.",
		}),
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "Passwords do not match",
		path: ["confirmPassword"],
	});

type FormSchema = z.infer<typeof formSchema>;

interface SignUpEmailAndPhoneProps {
	promo_code?: string;
}

const SignUpEmailAndPhone = ({ promo_code }: SignUpEmailAndPhoneProps) => {
	const navigate = useNavigate();
	const { toast } = useToast();
	const { setUserData } = useContext(MainContext);
	const registerMutation = useSignUpUser();
	const [progress, setProgress] = useState(0);
	const [showSignInDialog, setShowSignInDialog] = useState(false);
	const phoneInputRef = useRef<HTMLInputElement>(null);

	const form = useForm<FormSchema>({
		resolver: zodResolver(formSchema),
		mode: "onBlur",
		defaultValues: {
			email: "",
			password: "",
			confirmPassword: "",
			phone: "",
			countryCode: undefined,
			ageConfirmed: false,
		},
	});

	useEffect(() => {
		const timer = setTimeout(() => setProgress(15), 20);
		return () => clearTimeout(timer);
	}, []);

	const handleSubmit = (values: FormSchema) => {
		const getCountry = Object.values(COUNTRIES).find(
			(country) => country.id === String(values.countryCode),
		);
		if (!getCountry) {
			form.setError("countryCode", {
				type: "manual",
				message: "Invalid country selected. Please select a valid country.",
			});
			return;
		}
		const localPhone = getCountry.secondary
			? removeDialCode(values.phone, getCountry.secondary)
			: values.phone;

		registerMutation.mutate(
			{
				email: values.email,
				password: values.password,
				promo_code,
				phone_global_country_id: values.countryCode,
				phone_number: Number.parseInt(localPhone),
				email_verification_callback: `${window.location.origin}/verify-email`,
			},
			{
				onSuccess: async () => {
					try {
						setUserData((prev) => ({
							...prev,
							email: values.email,
							phone_number: localPhone,
							global_country_id: values.countryCode,
							promo_code,
						}));
						navigate({
							to: "/sign_up/verify-account",
							search: { intent: "sign_up" },
						});
					} catch (_) {
						toast({
							title: "Verification Failed",
							description:
								"Failed to send verification codes. Please try again.",
							variant: "destructive",
						});
					}
				},
				onError: (error) => {
					if (error?.code === USER_ALREADY_EXISTS_ERROR_CODE) {
						setShowSignInDialog(true);
						return;
					}
					toast({
						title: "Registration Failed",
						description: error.message,
						variant: "destructive",
					});
				},
			},
		);
	};

	return (
		<>
			<PageTransition>
				<div className="flex flex-col items-center justify-between px-6 py-8 bg-white h-[100dvh]">
					<div className="flex flex-col items-center justify-center w-full pt-2">
						<div className="w-full mb-2 max-w-16">
							<Progress value={progress} />
						</div>
						<h1 className="mb-6 text-4xl font-bold text-textDark">
							Getting Started
						</h1>
					</div>

					<div className="w-full max-w-sm space-y-6">
						<Form {...form}>
							<form
								className="w-full max-w-sm space-y-6"
								onSubmit={form.handleSubmit(handleSubmit)}
							>
								<FormField
									control={form.control}
									name="email"
									render={({ field }) => (
										<FloatingInput
											label="Email address"
											type="text"
											value={field.value}
											onChange={field.onChange}
											error={form.formState.errors.email}
										/>
									)}
								/>
								<FormField
									control={form.control}
									name="password"
									render={({ field }) => (
										<PasswordWithRequirements
											label="Password"
											value={field.value ?? ""}
											onChange={field.onChange}
											error={form.formState.errors.password}
										/>
									)}
								/>
								<FormField
									control={form.control}
									name="confirmPassword"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<FloatingInput
													type="password"
													label="Confirm Password"
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="phone"
									render={({ field }) => (
										<FormControl>
											<PhoneInputField
												placeholder="Phone number"
												ref={phoneInputRef}
												value={field.value}
												onChange={field.onChange}
												onBlur={field.onBlur}
												onCountryChange={(countryCode) =>
													form.setValue("countryCode", countryCode)
												}
												onError={(message) =>
													form.setError("phone", { type: "manual", message })
												}
											/>
										</FormControl>
									)}
								/>
								<FormField
									control={form.control}
									name="countryCode"
									render={({ field }) => (
										<FormControl>
											<input
												type="hidden"
												value={field.value ?? ""}
												onChange={field.onChange}
												onBlur={field.onBlur}
											/>
										</FormControl>
									)}
								/>
								{form.formState.errors.phone ? (
									<p className="text-[0.8rem] font-medium text-utility-red">
										{form.formState.errors.phone.message}
									</p>
								) : form.formState.errors.countryCode ? (
									<p className="text-[0.8rem] font-medium text-utility-red">
										{form.formState.errors.countryCode.message}
									</p>
								) : null}
								<p className="text-xs flex items-center gap-2 text-textLight -mb-2 text-center">
									<CheckCircle className="size-4" /> {PHONE_NUMBER_STATEMENT}
								</p>
								<FormField
									control={form.control}
									name="ageConfirmed"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-row items-center space-x-2 space-y-0">
												<FormControl>
													<Checkbox
														id="age-confirmed"
														checked={field.value}
														onCheckedChange={field.onChange}
													/>
												</FormControl>
												<FormLabel
													htmlFor="age-confirmed"
													className="text-sm  font-medium text-textDark mb-0 cursor-pointer"
												>
													I confirm that I am 21 years of age or older.
												</FormLabel>
											</div>
											<FormMessage className="ml-8" />
										</FormItem>
									)}
								/>
								<SubmitButton
									testId="create-account-button-email"
									mutationStatus={registerMutation.status}
									className="w-full"
								>
									Submit
								</SubmitButton>
							</form>
						</Form>

						<p className="text-sm text-center text-textDark">
							Already have an account?{" "}
							<Button
								variant="link"
								size="compact"
								className="font-semibold cursor-pointer"
								onClick={() => navigate({ to: "/face-id" })}
							>
								Sign In
							</Button>
						</p>
					</div>
					<div className="w-full max-w-sm ">
						<a
							href={getHelpCenterUrl()}
							target="_blank"
							rel="noopener noreferrer"
							className="text-sm text-center cursor-pointer w-full block"
						>
							<span className="text-textDark">Having issues?</span>{" "}
							<span className="text-textDark font-semibold">
								Contact Support
							</span>
						</a>
						<div className="flex justify-center w-full">
							<p className="mt-4 text-sm font-medium text-center text-textLight w-full max-w-[300px]">
								<span>
									By creating an account you agree to our{" "}
									<a
										target="_blank"
										rel="noreferrer"
										className={buttonVariants({
											variant: "link",
											size: "compact",
										})}
										href={getTermsOfServiceUrl()}
									>
										Terms and Conditions
									</a>
								</span>
							</p>
						</div>
					</div>
				</div>
			</PageTransition>

			<Dialog open={showSignInDialog} onOpenChange={setShowSignInDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Account Already Exists</DialogTitle>
						<DialogDescription>
							An account with this email and phone already exists. Would you
							like to sign in instead?
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							size={"md"}
							variant="outline"
							onClick={() => setShowSignInDialog(false)}
						>
							Cancel
						</Button>
						<Button
							size={"md"}
							variant={"default"}
							onClick={() => {
								setShowSignInDialog(false);
								navigate({ to: "/sign-mail" });
							}}
						>
							Sign In
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default SignUpEmailAndPhone;
